/** @file /account view: email, password-reset trigger, AUTH-06 account delete. */
import { h, clear } from "./dom.js";
import { authStore } from "../core/auth-store.js";
import { resetPassword, mapAuthError } from "../persistence/auth.js";
import { showToast } from "./toast.js";
import { openConfirmDialog } from "./confirm-dialog.js";
import { openModal } from "./modal.js";
import { deleteAccountAndData } from "../persistence/account-delete.js";
import { downloadExport } from "../persistence/data-export.js";
import { listPlays } from "../persistence/plays-repo.js";
import { listTeams } from "../persistence/teams-repo.js";
import { getAuth } from "../persistence/firebase.js";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";
import { navigate } from "../core/router.js";

/**
 * @param {string} email
 * @returns {Promise<string | null>}
 */
function askPassword(email) {
  return openModal((close) => {
    const input = /** @type {HTMLInputElement} */ (
      h("input", { type: "password", required: true, class: "hb-auth-input" })
    );
    const submit = h("button", { type: "submit", class: "hb-btn hb-btn--primary min-h-11" }, [
      "Bestätigen",
    ]);
    const cancel = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Abbrechen"]);
    cancel.addEventListener("click", () => close(null));
    const form = h("form", { class: "flex flex-col gap-3" }, [
      h("h2", { class: "text-xl font-bold" }, ["Passwort bestätigen"]),
      h("p", { class: "text-sm text-neutral-400" }, [email]),
      input,
      h("div", { class: "flex gap-2 justify-end mt-2" }, [cancel, submit]),
    ]);
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      close(input.value);
    });
    setTimeout(() => input.focus(), 0);
    return form;
  });
}

/**
 * @param {string} expected
 * @returns {Promise<boolean>}
 */
function deleteConfirmTyped(expected) {
  return openModal((close) => {
    const input = /** @type {HTMLInputElement} */ (
      h("input", { type: "text", class: "hb-auth-input" })
    );
    const submit = /** @type {HTMLButtonElement} */ (
      h(
        "button",
        { type: "submit", class: "hb-btn hb-btn--danger min-h-11" },
        ["Konto löschen"],
      )
    );
    submit.disabled = true;
    input.addEventListener("input", () => {
      submit.disabled = input.value !== expected;
    });
    const cancel = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Abbrechen"]);
    cancel.addEventListener("click", () => close(false));
    const form = h("form", { class: "flex flex-col gap-3" }, [
      h("h2", { class: "text-xl font-bold" }, ["Konto wirklich löschen?"]),
      h("p", { class: "text-sm text-red-400" }, [
        "Alle Spielzüge und Teams werden unwiderruflich gelöscht.",
      ]),
      h("p", { class: "text-sm text-neutral-400" }, [
        `Tippe '${expected}' zum Bestätigen ein:`,
      ]),
      input,
      h("div", { class: "flex gap-2 justify-end mt-2" }, [cancel, submit]),
    ]);
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (input.value === expected) {
        close(true);
      }
    });
    return form;
  }).then((v) => v === true);
}

/**
 * @param {HTMLElement} host
 */
export function mountAccountView(host) {
  clear(host);
  const { user } = authStore.get();
  const email = user?.email ?? "(unbekannt)";

  const container = h("div", { class: "max-w-xl mx-auto p-6 flex flex-col gap-6" });
  host.appendChild(container);

  // Profile card
  const profile = h("div", { class: "p-6 bg-neutral-800 rounded-2xl flex flex-col gap-3" }, [
    h("h2", { class: "text-xl font-bold" }, ["Mein Konto"]),
    h("p", { class: "text-sm text-neutral-400" }, ["E-Mail:"]),
    h("p", null, [email]),
  ]);
  const resetBtn = h("button", { type: "button", class: "hb-btn min-h-11" }, [
    "Passwort-Reset-Mail senden",
  ]);
  resetBtn.addEventListener("click", async () => {
    if (!user?.email) {
      return;
    }
    try {
      await resetPassword(user.email);
      showToast("E-Mail zum Zurücksetzen gesendet");
    } catch (err) {
      showToast(mapAuthError(err));
    }
  });
  profile.appendChild(resetBtn);
  container.appendChild(profile);

  // Datenexport (DSGVO Art. 20)
  const exportCard = h(
    "div",
    { class: "p-6 bg-neutral-800 rounded-2xl flex flex-col gap-3" },
    [
      h("h2", { class: "text-xl font-bold" }, ["Datenexport (DSGVO Art. 20)"]),
      h("p", { class: "text-sm text-neutral-300" }, [
        "Lade alle deine Spielzüge und Teams als JSON-Datei herunter.",
      ]),
    ],
  );
  const exportBtn = /** @type {HTMLButtonElement} */ (
    h("button", { type: "button", class: "hb-btn min-h-11" }, [
      "Meine Daten exportieren (JSON)",
    ])
  );
  exportBtn.addEventListener("click", async () => {
    const current = authStore.get().user;
    if (!current) {
      showToast("Nicht angemeldet");
      return;
    }
    exportBtn.disabled = true;
    try {
      const [plays, teams] = await Promise.all([
        listPlays(current.uid),
        listTeams(current.uid),
      ]);
      downloadExport(
        {
          uid: current.uid,
          email: current.email ?? null,
          displayName: current.displayName ?? null,
        },
        plays,
        teams,
      );
      showToast("Export gestartet");
    } catch (err) {
      console.warn("[account] export failed", err);
      showToast(mapAuthError(err) || "Export fehlgeschlagen");
    } finally {
      exportBtn.disabled = false;
    }
  });
  exportCard.appendChild(exportBtn);
  container.appendChild(exportCard);

  // Danger zone
  const danger = h(
    "div",
    { class: "p-6 rounded-2xl flex flex-col gap-3 border-2 border-red-900 bg-neutral-800" },
    [
      h("h2", { class: "text-xl font-bold text-red-400" }, ["Gefahrenzone"]),
      h("p", { class: "text-sm text-neutral-300" }, [
        "Konto-Löschung entfernt alle Spielzüge und Teams unwiderruflich.",
      ]),
    ],
  );
  const deleteBtn = h("button", { type: "button", class: "hb-btn hb-btn--danger min-h-11" }, [
    "Konto löschen",
  ]);
  deleteBtn.addEventListener("click", async () => {
    const initial = await openConfirmDialog({
      title: "Konto löschen?",
      body: "Dieser Schritt ist unumkehrbar. Alle Daten werden gelöscht.",
      destructive: true,
      confirmLabel: "Weiter",
    });
    if (!initial) {
      return;
    }
    const typed = await deleteConfirmTyped("DELETE");
    if (!typed) {
      return;
    }
    const auth = getAuth();
    const current = auth?.currentUser;
    if (!current) {
      showToast("Nicht angemeldet");
      return;
    }
    try {
      await deleteAccountAndData(current, async (providerId) => {
        if (providerId === "google.com") {
          await reauthenticateWithPopup(current, new GoogleAuthProvider());
          return;
        }
        const pw = await askPassword(current.email ?? "");
        if (!pw) {
          throw new Error("Reauth cancelled");
        }
        if (!current.email) {
          throw new Error("No email on user");
        }
        const cred = EmailAuthProvider.credential(current.email, pw);
        await reauthenticateWithCredential(current, cred);
      });
      showToast("Konto gelöscht");
      navigate("editor");
    } catch (err) {
      console.warn("[account] delete failed", err);
      showToast(mapAuthError(err));
    }
  });
  danger.appendChild(deleteBtn);
  container.appendChild(danger);
}
